import { CalendarDate } from '@internationalized/date';
import {
  enumLoadSortOption,
  LoadSortOption,
  LoadStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { brokeredLoadsListQueryAtom } from '../_graphql/queryBrokeredLoadsList';
import { BrokeredLoadRecord } from '../types/brokeredLoad';

import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedStatus = atomFromQueryString.string<string>('s');
export const $selectedFacility = atomFromQueryString.string('f');
export const $selectedSupplierIds =
  atomFromQueryString.stringSet('supplierIds');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $dateRange = atomFromQueryString.dateRange();

// 🟣🟣🟣 COLUMN FILTER PARAMS
export const $columnDateRange = atomFromQueryString.dateRange();
export const $columnSelectedSupplierIds =
  atomFromQueryString.stringSet('colSuppliers');
export const $columnSelectedFacilityIds =
  atomFromQueryString.stringSet('colFacilities');
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<LoadStatus>('colStatuses');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isCreateDialogOpen = atom<boolean>(false);
export const $selectedLoad = atom<BrokeredLoadRecord | null>(null);
export const $isVoidDialogOpen = atom<boolean>(false);
export const $selectedLoadIdForShipped = atom<string | null>(null);
export const $selectedLoadIdForReceived = atom<string | null>(null);
export const $currentSorting = atom<LoadSortOption[]>([
  enumLoadSortOption.CreatedAtDesc,
]);

// 🟢🟢🟢 REMOTE DATA
export const $remoteBrokeredLoads = brokeredLoadsListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedFacility = get($selectedFacility);
  const supplierIds = Array.from(get($selectedSupplierIds));
  const dateRange = get($dateRange);
  const limit = get($limit);
  const offset = get($offset);
  const sortingParams = get($currentSorting);

  // Column filters
  const columnDateRange = get($columnDateRange);
  const columnSelectedSupplierIds = get($columnSelectedSupplierIds);
  const columnSelectedFacilityIds = get($columnSelectedFacilityIds);
  const columnSelectedStatuses = get($columnSelectedStatuses);

  const params: {
    searchTerm?: string;
    status: LoadStatus[];
    facilityId?: string;
    facilityIds?: string[];
    supplierIds?: string[];
    fromExpectedShipDate?: CalendarDate;
    toExpectedShipDate?: CalendarDate;
    fromColumnDate?: CalendarDate;
    toColumnDate?: CalendarDate;
    columnStatuses?: LoadStatus[];
    limit: number;
    offset: number;
    sorting: LoadSortOption[];
  } = {
    limit,
    offset,
    sorting: sortingParams,
    status: [],
  };

  if (searchTerm?.trim()) params.searchTerm = searchTerm;
  if (supplierIds.length > 0) params.supplierIds = supplierIds;
  if (selectedFacility && selectedFacility !== 'all')
    params.facilityId = selectedFacility;
  if (dateRange?.start) {
    params.fromExpectedShipDate = dateRange.start as CalendarDate;
  }
  if (dateRange?.end) {
    params.toExpectedShipDate = dateRange.end as CalendarDate;
  }

  // Column filters
  if (columnSelectedSupplierIds.size > 0) {
    params.supplierIds = Array.from(columnSelectedSupplierIds);
  }
  if (columnSelectedFacilityIds.size > 0) {
    params.facilityIds = Array.from(columnSelectedFacilityIds);
  }
  if (columnSelectedStatuses.size > 0) {
    params.columnStatuses = Array.from(columnSelectedStatuses) as LoadStatus[];
  }
  if (columnDateRange?.start) {
    params.fromColumnDate = columnDateRange.start as CalendarDate;
  }
  if (columnDateRange?.end) {
    params.toColumnDate = columnDateRange.end as CalendarDate;
  }

  return {
    variables: params,
  };
});

// 🟢🟢🟢 COMPUTED DATA -> filters active
export const $filtersActive = atom((get) => {
  const searchTerm = get($searchTerm);
  const selectedFacility = get($selectedFacility);
  const supplierIds = get($selectedSupplierIds);
  const dateRange = get($dateRange);

  return (
    (searchTerm !== undefined && searchTerm?.trim() !== '') ||
    (selectedFacility !== undefined && selectedFacility !== 'all') ||
    supplierIds.size > 0 ||
    !!dateRange
  );
});
